import { Card } from "@/src/components/wrappers/Card";
import { Amenity, Booking, Operator } from "../../../model";
import { Button } from "@/src/components/ui/button";
import { YachtDetailsModal } from "@/src/app/Offers/EditOffer/YachtDetailsModal";
import { Separator } from "@radix-ui/react-separator";
import { parseISO, format } from "date-fns";
import { extractFromMultiName } from "@/src/app/Locations/utils";
import React, { useState } from "react";
import { EditBookingCharterInfoModal } from "../../../EditBookingCharterInfo";
import {
  getDateByProvider,
  parseDateIntoTime,
} from "@/src/app/Offers/EditOffer/ChangeYachtDetailsModal/ChangeYachtDetailsModal";
import { formatDateUTC } from "../../../../../helpers/formatDateUTC";
import YachtLabel from "@/src/components/ui/yacht-label";
import { resolveDate } from "./YachtSection.utils";
import { ChangeYachtModal } from "./ChangeYachtModal";
import { charterProfile } from "../../../charterProfile";

const SOURCE: Record<string, string> = { platform: "Client booked online", broker_created: "Broker-created", imported: "Imported" };
const humanStatus = (v: string | null | undefined) => (v ? v.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "Booking");
const Tag = ({ cls, children }: { cls: string; children: React.ReactNode }) => <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;

const YachtSection = ({
  booking,
  handleYachtDetailsModal,
  yachtDetailsOpen,
  yachtId,
  setYachtDetailsOpen,
  amenities,
  operator,
}: {
  booking: Booking;
  handleYachtDetailsModal: (id: string) => void;
  yachtDetailsOpen: boolean;
  yachtId: string | null;
  setYachtDetailsOpen: (value: boolean) => void;
  amenities: Amenity[];
  operator?: Operator;
}) => {
  const includes = [...amenities]?.filter((am) => am.included);
  const obligatories = [...amenities]?.filter((am) => am.obligatory);
  const [updateCharterInfoOpen, setUpdateCharterInfoOpen] = useState(false);
  const [changeYachtOpen, setChangeYachtOpen] = useState(false);

  const startTime = resolveDate(
    booking.startDate,
    booking.offerYacht?.startDate,
    booking.offerYacht?.yacht?.provider,
    "start",
  );
  const endTime = resolveDate(booking.endDate, booking.offerYacht?.endDate, booking.offerYacht?.yacht?.provider, "end");
  const fmtDay = (d: string | undefined | null) => (d ? format(new Date(d), "d MMM yyyy") : null);
  const dateRange = [fmtDay(booking.startDate ?? booking.offerYacht.startDate), fmtDay(booking.endDate ?? booking.offerYacht.endDate)].filter(Boolean).join(" – ") || "dates tbc";
  // Check-in / check-out times used to have their own block; the design's meta line carries them.
  const hhmm = (d: string | undefined | null) => (d ? `${String(new Date(d).getUTCHours()).padStart(2, "0")}:${String(new Date(d).getUTCMinutes()).padStart(2, "0")}` : null);
  const times = [hhmm(booking.startDate ?? booking.offerYacht.startDate), hhmm(booking.endDate ?? booking.offerYacht.endDate)].every(Boolean) ? `in ${hhmm(booking.startDate ?? booking.offerYacht.startDate)} · out ${hhmm(booking.endDate ?? booking.offerYacht.endDate)}` : null;

  return (
    <Card className="w-full flex flex-col gap-y-6">
      <div className="flex flex-col gap-4 sm:flex-row">
        <img
          className="h-[150px] w-full shrink-0 rounded-lg object-cover sm:w-[230px]"
          src={booking.offerYacht.yacht.media[0]}
          alt={booking.offerYacht.yacht.name}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-2xl font-semibold leading-tight">{booking.offerYacht.yacht.name}</span>
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${/received|confirmed|checked|upcoming/i.test(booking.hubspotStatus ?? "") ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-700"}`}>
              {/received|confirmed|upcoming/i.test(booking.hubspotStatus ?? "") ? "✓ Confirmed" : humanStatus(booking.hubspotStatus)}
            </span>
            <span className="ml-auto flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); handleYachtDetailsModal(booking.offerYacht.yacht.id); }}>Yacht details</Button>
              {yachtDetailsOpen && <YachtDetailsModal yachtDetailsId={yachtId} open={yachtDetailsOpen} setOpen={setYachtDetailsOpen} />}
              <Button variant="ghost" size="sm" onClick={() => setChangeYachtOpen(true)}>⇄ Change yacht</Button>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-700">
            <span>
              {[booking.offerYacht.yacht.modelName, booking.offerYacht.yacht.year].filter(Boolean).join(" · ")}
              {" · "}{dateRange}
              {times && <span className="text-slate-500"> ({times})</span>}
              {" · "}{booking.guests || booking.offerYacht.yacht.maxPeople} guests
              {" · "}{extractFromMultiName(booking.startLocation?.names ?? booking.offerYacht.startLocation?.names, "?")} → {extractFromMultiName(booking.endLocation?.names ?? booking.offerYacht.endLocation?.names, "?")}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setUpdateCharterInfoOpen(true)}>✎ Edit dates &amp; guests</Button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Tag cls="bg-blue-100 text-blue-800">{charterProfile(booking).label}</Tag>
            <Tag cls="bg-slate-100 text-slate-700">{String(booking.brand ?? "").toUpperCase()}</Tag>
            <Tag cls="bg-slate-100 text-slate-700">{SOURCE[String(booking.source ?? "")] ?? (booking.importDetails ? "Imported" : "Broker-created")}</Tag>
            {booking.slug && <Tag cls="bg-teal-100 text-teal-800">{booking.slug}</Tag>}
            <YachtLabel apiDateUpdated={booking.offerYacht?.yacht?.apiDateUpdated} apiStatus={booking.offerYacht?.yacht?.apiStatus} />
          </div>
        </div>
      </div>
      {changeYachtOpen && (
        <ChangeYachtModal
          open={changeYachtOpen}
          setOpen={setChangeYachtOpen}
          bookingId={booking.id}
          currentYachtId={booking.offerYacht.yacht.id}
          currentYachtName={booking.offerYacht.yacht.name}
          brand={booking.brand}
        />
      )}
      {updateCharterInfoOpen && (
        <EditBookingCharterInfoModal
          open={updateCharterInfoOpen}
          setOpen={setUpdateCharterInfoOpen}
          initialData={{
            startDate: booking.startDate ?? booking.offerYacht.startDate,
            endDate: booking.endDate ?? booking.offerYacht.endDate,
            startTime: startTime,
            endTime: endTime,
            startLocation: booking.startLocation ?? booking.offerYacht.startLocation!,
            endLocation: booking.endLocation ?? booking.offerYacht.endLocation!,
          }}
          bookingId={booking?.id}
          offerYachtId={booking?.offerYacht.id}
        />
      )}
    </Card>
  );
};

export default YachtSection;
